// React
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Enforces a target i18n language and returns readiness once initialization finishes.
 * @param {string} [language="en"] Language code to enforce (for example, "en").
 * @param {string} [logContext="Portal"] Context label included in initialization error logs.
 * @returns {boolean} True when language initialization is complete.
 */
const useEnforcedLanguage = (language = "en", logContext = "Portal") => {
  const { i18n } = useTranslation();
  const [isLanguageReady, setIsLanguageReady] = useState(
    i18n.resolvedLanguage === language
  );

  useEffect(() => {
    let active = true;

    const enforceLanguage = async () => {
      try {
        if (i18n.resolvedLanguage !== language) {
          await i18n.changeLanguage(language);
        }
      } catch (error) {
        console.error(
          `Failed to initialize ${logContext} language to ${language}`,
          error
        );
      } finally {
        if (active) setIsLanguageReady(true);
      }
    };

    enforceLanguage();

    return () => {
      active = false;
    };
  }, [i18n, language, logContext]);

  return isLanguageReady;
};

export default useEnforcedLanguage;
